import { useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { CalendarIcon, Clock, Users, Phone, Mail, User, Check } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASSETS = {
  interior: "https://customer-assets-v7afamib.emergentagent.net/job_shamrock-preview/artifacts/da0mgety_WhatsApp%20Image%202026-07-12%20at%201.19.30%20PM%20%285%29.jpeg",
};

const timeSlots = [
  "16:00", "16:30", "17:00", "17:30", "18:00", "18:30",
  "19:00", "19:30", "20:00", "20:30", "21:00", "21:30",
  "22:00", "22:30", "23:00", "23:30"
];

const guestOptions = Array.from({ length: 12 }, (_, i) => i + 1);

const ReservationsPage = () => {
  const [date, setDate] = useState(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    time: '',
    guests: '',
    special_requests: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date || !formData.name || !formData.email || !formData.phone || !formData.time || !formData.guests) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const reservationData = {
        ...formData,
        date: format(date, 'yyyy-MM-dd'),
        guests: parseInt(formData.guests)
      };
      
      await axios.post(`${API}/reservations`, reservationData);
      
      setIsSuccess(true);
      toast.success('Reservation submitted successfully!');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        time: '',
        guests: '',
        special_requests: ''
      });
      setDate(null);
      
      setTimeout(() => setIsSuccess(false), 5000);
    } catch {
      toast.error('Failed to submit reservation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="reservations-page" className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-[#050a09]">
        <div className="absolute inset-0 opacity-30">
          <img
            src={ASSETS.interior}
            alt="Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050a09] via-[#050a09]/80 to-[#050a09]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-4 font-['Manrope']">
            Reserve Your Spot
          </p>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-6xl font-bold text-[#E8E6D9] mb-4">
            Book a <span className="text-[#D4AF37]">Table</span>
          </h1>
          <p className="text-[#A3A3A3] font-['Manrope'] text-lg">
            Secure your evening at The Shamrock
          </p>
        </div>
      </section>

      {/* Reservation Form Section */}
      <section className="py-16 md:py-24 bg-[#0b1412]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            {/* Form */}
            <div className="bg-[#050a09] border border-white/5 rounded-sm p-8 md:p-10">
              {isSuccess ? (
                <div data-testid="reservation-success" className="text-center py-12">
                  <div className="w-20 h-20 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="text-[#D4AF37]" size={40} />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#E8E6D9] mb-4">
                    Reservation Received!
                  </h3>
                  <p className="text-[#A3A3A3] font-['Manrope']">
                    We&apos;ll send a confirmation to your email shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#E8E6D9] mb-6">
                    Reservation Details
                  </h2>

                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#E8E6D9] font-['Manrope'] flex items-center gap-2">
                      <User size={16} className="text-[#D4AF37]" />
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="input-name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your name"
                      className="bg-black/20 border-white/10 text-[#E8E6D9] placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] h-12"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#E8E6D9] font-['Manrope'] flex items-center gap-2">
                      <Mail size={16} className="text-[#D4AF37]" />
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="input-email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      className="bg-black/20 border-white/10 text-[#E8E6D9] placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] h-12"
                      required
                    />
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-[#E8E6D9] font-['Manrope'] flex items-center gap-2">
                      <Phone size={16} className="text-[#D4AF37]" />
                      Phone *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      data-testid="input-phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+41 XX XXX XX XX"
                      className="bg-black/20 border-white/10 text-[#E8E6D9] placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] h-12"
                      required
                    />
                  </div>

                  {/* Date & Time Row */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    {/* Date */}
                    <div className="space-y-2">
                      <Label className="text-[#E8E6D9] font-['Manrope'] flex items-center gap-2">
                        <CalendarIcon size={16} className="text-[#D4AF37]" />
                        Date *
                      </Label>
                      <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            data-testid="date-picker-btn"
                            className="w-full h-12 px-4 bg-black/20 border border-white/10 rounded-md text-left text-[#E8E6D9] hover:border-[#D4AF37]/50 transition-colors flex items-center justify-between"
                          >
                            <span className={date ? 'text-[#E8E6D9]' : 'text-white/30'}>
                              {date ? format(date, 'PPP') : 'Select date'}
                            </span>
                            <CalendarIcon size={18} className="text-[#D4AF37]" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-[#0b1412] border-[#D4AF37]/30" align="start">
                          <Calendar
                            mode="single"
                            selected={date}
                            onSelect={(newDate) => {
                              setDate(newDate);
                              setIsCalendarOpen(false);
                            }}
                            disabled={(date) => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              return date < today;
                            }}
                            initialFocus
                            className="bg-[#0b1412]"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Time */}
                    <div className="space-y-2">
                      <Label className="text-[#E8E6D9] font-['Manrope'] flex items-center gap-2">
                        <Clock size={16} className="text-[#D4AF37]" />
                        Time *
                      </Label>
                      <Select value={formData.time} onValueChange={(v) => handleSelectChange('time', v)}>
                        <SelectTrigger 
                          data-testid="time-select"
                          className="h-12 bg-black/20 border-white/10 text-[#E8E6D9] focus:ring-[#D4AF37]"
                        >
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0b1412] border-[#D4AF37]/30">
                          {timeSlots.map((time) => (
                            <SelectItem 
                              key={time} 
                              value={time}
                              className="text-[#E8E6D9] focus:bg-[#D4AF37]/20 focus:text-[#E8E6D9]"
                            >
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Guests */}
                  <div className="space-y-2">
                    <Label className="text-[#E8E6D9] font-['Manrope'] flex items-center gap-2">
                      <Users size={16} className="text-[#D4AF37]" />
                      Number of Guests *
                    </Label>
                    <Select value={formData.guests} onValueChange={(v) => handleSelectChange('guests', v)}>
                      <SelectTrigger 
                        data-testid="guests-select"
                        className="h-12 bg-black/20 border-white/10 text-[#E8E6D9] focus:ring-[#D4AF37]"
                      >
                        <SelectValue placeholder="Select guests" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0b1412] border-[#D4AF37]/30">
                        {guestOptions.map((num) => (
                          <SelectItem 
                            key={num} 
                            value={num.toString()}
                            className="text-[#E8E6D9] focus:bg-[#D4AF37]/20 focus:text-[#E8E6D9]"
                          >
                            {num} {num === 1 ? 'Guest' : 'Guests'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Special Requests */}
                  <div className="space-y-2">
                    <Label htmlFor="special_requests" className="text-[#E8E6D9] font-['Manrope']">
                      Special Requests
                    </Label>
                    <Textarea
                      id="special_requests"
                      name="special_requests"
                      data-testid="input-special-requests"
                      value={formData.special_requests}
                      onChange={handleInputChange}
                      placeholder="Any special requests or dietary requirements..."
                      className="bg-black/20 border-white/10 text-[#E8E6D9] placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] min-h-[100px]"
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    data-testid="submit-reservation-btn"
                    disabled={isSubmitting}
                    className="w-full bg-[#D4AF37] text-[#050a09] py-4 rounded-sm font-bold tracking-wider uppercase hover:bg-[#E5C568] transition-all duration-300 btn-glow disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Confirm Reservation'}
                  </button>

                  <p className="text-[#525252] text-xs text-center font-['Manrope']">
                    By making a reservation, you agree to our terms and conditions.
                  </p>
                </form>
              )}
            </div>

            {/* Info Panel */}
            <div className="space-y-8">
              {/* Image */}
              <div className="relative aspect-[4/3] rounded-sm overflow-hidden">
                <img
                  src={ASSETS.interior}
                  alt="The Shamrock Interior"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050a09]/80 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#E8E6D9]">
                    Your Table Awaits
                  </h3>
                </div>
              </div>

              {/* Info Cards */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-[#050a09] border border-white/5 rounded-sm p-6">
                  <Clock size={24} className="text-[#D4AF37] mb-3" />
                  <h4 className="font-['Playfair_Display'] text-lg text-[#E8E6D9] mb-2">Opening Hours</h4>
                  <div className="text-[#A3A3A3] text-sm font-['Manrope'] space-y-1">
                    <p>Mon - Thu: 11:00 - 23:00</p>
                    <p>Friday: 11:00 - 01:00</p>
                    <p>Saturday: 16:00 - 01:00</p>
                    <p>Sunday: 16:00 - 23:00</p>
                  </div>
                </div>
                <div className="bg-[#050a09] border border-white/5 rounded-sm p-6">
                  <Phone size={24} className="text-[#D4AF37] mb-3" />
                  <h4 className="font-['Playfair_Display'] text-lg text-[#E8E6D9] mb-2">Contact Us</h4>
                  <div className="text-[#A3A3A3] text-sm font-['Manrope'] space-y-1">
                    <p>033 722 70 00</p>
                    <p>bern@theshamrock.ch</p>
                    <p>Schwarztorstrasse 11, 3007 Bern</p>
                  </div>
                </div>
              </div>

              {/* Note */}
              <div className="bg-[#1B4D3E]/20 border border-[#1B4D3E]/30 rounded-sm p-6">
                <p className="text-[#E8E6D9] font-['Manrope'] text-sm leading-relaxed">
                  <span className="text-[#D4AF37] font-semibold">Note:</span> For groups larger than 12 or special events, please contact us directly at{' '}
                  <a href="mailto:bern@theshamrock.ch" className="text-[#D4AF37] hover:underline">
                    bern@theshamrock.ch
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReservationsPage;
